import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import CanPerform from 'components/shared/CanPerform';
import Modal from 'components/ui/Modal';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import { CLONE_EXTERNAL_TICKET } from 'constants/afterSalesShortcuts';
import { bookingShape, historyShape } from 'constants/propTypes';
import { CONFIRM_BOOKING, REVERT_CHANGE, UPDATE_SEGMENTS } from 'store/mutations';
import { bookingStatus } from 'types/booking';
import { getBookingJourneysURL, getPendingPaymentURL, getRefundMethodsURL } from 'utils/urls';

import OVERRIDE_FEES from '../../../Refunds/graphql/OVERRIDE_FEES';
import bookingQuery from '../../queries/booking';
import { StyledColumn } from '../../AfterSalesWarning/AfterSalesWarning.styles';
import { CtaButton } from '../AfterSalesCtas.styles';

import { CANCEL_EXTERNAL_TICKETS, CLONE_EXTERNAL_TICKETS_JOURNEY_SEARCH } from './CloneExternalTicketsCta.graphql';
import {
  buildCloneSearchVariables,
  buildCloneSegment,
  findCloneFare,
  getCloneWarning,
  getSelectedExternalLeg,
  hasCloneReplacement,
  isCloneSelectionEligible
} from './CloneExternalTicketsCta.utils';

const OVERRIDE_REASON = 'Clone external tickets';
const hasMatchingBookingReference = (actual, expected) => typeof actual === 'string' && typeof expected === 'string' && actual === expected;

const CloneExternalTicketsCta = ({ booking, disabled, history, refetchBooking, selectedItemRefs }) => {
  const client = useApolloClient();
  const [warning, setWarning] = useState(null);
  const [missingTrain, setMissingTrain] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const selection = getSelectedExternalLeg({ booking, selectedItemRefs });
  const isDisabled = disabled || loading || !isCloneSelectionEligible({ booking, selection });
  const membershipNumber = new URLSearchParams(history?.location?.search || '').get('membershipNumber');

  const closeWarning = () => setWarning(null);
  const closeMissingTrain = () => setMissingTrain(false);
  const closeError = () => setError(false);

  const refreshBooking = async () => {
    if (refetchBooking) {
      const result = await refetchBooking();

      return result?.data?.booking || result?.booking;
    }

    const result = await client.query({
      query: bookingQuery,
      variables: { reference: booking.reference },
      fetchPolicy: 'network-only'
    });

    return result.data.booking;
  };

  const revertClone = async () => {
    try {
      await client.mutate({
        mutation: REVERT_CHANGE,
        variables: { bookingReference: booking.reference }
      });
      await refreshBooking();
    } catch (revertError) {
      // The original error is more useful to the advisor; a refresh on the next visit will expose the current booking state.
    }
  };

  const completeClone = async (updatedBooking, onCommitted) => {
    const { reference, currency, totalPriceToBePaid } = updatedBooking;
    const balance = Number(totalPriceToBePaid);

    if (!reference || !currency || totalPriceToBePaid === null || totalPriceToBePaid === undefined || !Number.isFinite(balance)) {
      throw new Error('The updated booking did not contain a valid balance');
    }

    if (balance > 0) {
      onCommitted();
      history.push(getPendingPaymentURL(reference, currency, membershipNumber));

      return;
    }

    const confirmResult = await client.mutate({
      mutation: CONFIRM_BOOKING,
      variables: { bookingReference: reference }
    });
    const confirmedBooking = confirmResult?.data?.confirmBooking;
    const hasValidConfirmationStatus =
      confirmedBooking?.status === bookingStatus.CONFIRMED || (balance < 0 && confirmedBooking?.status === bookingStatus.PENDING_REFUND);

    if (!hasMatchingBookingReference(confirmedBooking?.reference, reference) || !hasValidConfirmationStatus) {
      throw new Error('The cloned booking could not be confirmed');
    }

    onCommitted();

    if (refetchBooking) {
      try {
        await refetchBooking();
      } catch (refetchError) {
        // The clone is already confirmed, so navigation should continue even if this refresh fails.
      }
    }

    if (balance < 0) {
      history.push(getRefundMethodsURL(reference, currency, membershipNumber));

      return;
    }

    history.push(getBookingJourneysURL(reference, membershipNumber));
  };

  const cloneTickets = async () => {
    closeWarning();
    setError(false);
    setLoading(true);
    let bookingWasChanged = false;

    try {
      const searchResult = await client.query({
        query: CLONE_EXTERNAL_TICKETS_JOURNEY_SEARCH,
        variables: buildCloneSearchVariables({ booking, selection }),
        fetchPolicy: 'network-only'
      });
      const match = findCloneFare(searchResult.data, selection.leg);

      if (!match) {
        setMissingTrain(true);

        return;
      }

      const passengerIds = selection.products.map(({ passengerId }) => passengerId);
      const segment = buildCloneSegment({
        match,
        direction: selection.leg.direction,
        passengerIds
      });

      if (!segment) {
        throw new Error('The replacement fare did not contain the selected passengers');
      }

      bookingWasChanged = true;
      const cancelResult = await client.mutate({
        mutation: CANCEL_EXTERNAL_TICKETS,
        variables: {
          bookingReference: booking.reference,
          itemRefs: selection.products.map(({ itemRef }) => String(itemRef)),
          overrideReason: OVERRIDE_REASON,
          overrideAftersalesRules: true
        }
      });

      if (cancelResult?.data?.cancelSpecificBookingItems?.cancellable !== true) {
        throw new Error('The selected external tickets could not be cancelled');
      }

      const overrideResult = await client.mutate({
        mutation: OVERRIDE_FEES,
        variables: {
          bookingReference: booking.reference,
          overrideReason: OVERRIDE_REASON,
          waiveRepricing: false,
          waiveHotelFees: false,
          waiveTrainFees: true
        }
      });

      if (!hasMatchingBookingReference(overrideResult?.data?.overridePendingCancellationBookingPrice?.reference, booking.reference)) {
        throw new Error('The cancellation fees could not be waived');
      }

      const updateResult = await client.mutate({
        mutation: UPDATE_SEGMENTS,
        variables: {
          currency: booking.currency,
          bookingReference: booking.reference,
          segments: [segment]
        }
      });

      if (!hasMatchingBookingReference(updateResult?.data?.updateBookingSegments?.reference, booking.reference)) {
        throw new Error('The replacement tickets could not be added');
      }

      const updatedBooking = await refreshBooking();

      if (!updatedBooking || !hasCloneReplacement({ booking: updatedBooking, segment })) {
        throw new Error('The replacement tickets could not be verified');
      }

      await completeClone(updatedBooking, () => {
        bookingWasChanged = false;
      });
    } catch (cloneError) {
      if (bookingWasChanged) {
        await revertClone();
      }

      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const openWarning = () => {
    if (isDisabled) {
      return;
    }

    setWarning(getCloneWarning(selection));
  };

  const missingTrainMessageId = `cloneExternalTickets.missingTrain${selection?.leg?.direction === 'inbound' ? 'Inbound' : 'Outbound'}`;
  const departureDate = moment(selection?.leg?.timing?.date || selection?.leg?.timing?.isoDate).format('DD MMM YYYY');

  return (
    <>
      <Modal
        isOpen={Boolean(warning)}
        onClose={closeWarning}
        title={<FormattedMessage id="cloneExternalTickets.title" />}
        subtitle={warning ? <FormattedMessage id={`cloneExternalTickets.warning${warning}`} /> : null}
      >
        {warning > 1 && (
          <Row>
            <StyledColumn span={4}>
              <ConfirmButton data-cy="clone-external-tickets-confirm" disabled={loading} loading={loading} onClick={cloneTickets}>
                <FormattedMessage id="interaction.yes" />
              </ConfirmButton>
            </StyledColumn>
            <StyledColumn span={4}>
              <CancelButton data-cy="clone-external-tickets-cancel" disabled={loading} onClick={closeWarning}>
                <FormattedMessage id="buttons.cancel" />
              </CancelButton>
            </StyledColumn>
          </Row>
        )}
      </Modal>

      <Modal
        isOpen={missingTrain}
        onClose={closeMissingTrain}
        title={<FormattedMessage id="cloneExternalTickets.title" />}
        subtitle={
          <FormattedMessage id={missingTrainMessageId} values={{ trainNumber: selection?.leg?.trainNumber, date: departureDate }} />
        }
      />

      <Modal
        isOpen={error}
        onClose={closeError}
        title={<FormattedMessage id="cloneExternalTickets.title" />}
        subtitle={<FormattedMessage id="cloneExternalTickets.error" />}
      />

      <CanPerform afterSalesShortcut={CLONE_EXTERNAL_TICKET}>
        {(isEligible) =>
          isEligible && (
            <CtaButton
              type="button"
              data-cy="clone-external-tickets-cta"
              disabled={isDisabled}
              loading={loading}
              label={<FormattedMessage id="afterSales.cloneExternalTickets" />}
              onClick={openWarning}
            />
          )
        }
      </CanPerform>
    </>
  );
};

CloneExternalTicketsCta.propTypes = {
  booking: bookingShape.isRequired,
  disabled: PropTypes.bool,
  history: historyShape,
  refetchBooking: PropTypes.func,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default CloneExternalTicketsCta;
